import { useEffect, useState } from "react";
import { WarehouseOutwardModel } from "../model/warehouse_outward_model";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { fetchPut } from "../../../../service/api_client";
import {
  fetchGet,
  fetchPost,
} from "../../content_panel/services/serviceClient";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  formatAddress,
  getOutwardDestinationLabel,
  getOutwardKazamWarehouseId,
  getOutwardSourceAddress,
  getOutwardSourceLabel,
} from "../warehouse_helper";

type Props = {
  open: boolean;
  onClose: () => void;
  outward: WarehouseOutwardModel;
  onSuccess: () => void;
};

type DCUrls = {
  pdfDownloadUrl: string;
  imagePreviewUrl: string;
  fileName?: string;
  dcNo?: string;
};

export default function GenerateDCModal({
  open,
  onClose,
  outward,
  onSuccess,
}: Props) {
  const [step, setStep] = useState<"FORM" | "PREVIEW">("FORM");
  const [loading, setLoading] = useState(false);
  const [dcUrls, setDcUrls] = useState<DCUrls | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { showToast } = useToast();
  const [poNumber, setPoNumber] = useState("");
  const [narration, setNarration] = useState("");
  const [errors, setErrors] = useState<{
    narration?: string;
    dcSeq?: string;
    gst?: string;
  }>({});
  const [gst_address, set_gst_address] = useState<any>();
  const [selectedGst, setSelectedGst] = useState<any>(null);
  const [dcPrefix, setDcPrefix] = useState("");
  const [dcSeq, setDcSeq] = useState("");
  const [suggestedSeq, setSuggestedSeq] = useState("");
  const kazamWarehouseId = getOutwardKazamWarehouseId(outward);

  const resetFormState = () => {
    setStep("FORM");
    setDcUrls(null);
    setImageLoaded(false);
    setImageError(false);
    setPoNumber("");
    setNarration("");
    setErrors({});
    setSelectedGst(null);
    setDcPrefix("");
    setDcSeq("");
    setSuggestedSeq("");
  };

  useEffect(() => {
    if (!open) {
      resetFormState();
      return;
    }

    setPoNumber("");
    setNarration("");
    setErrors({});
    setDcUrls(null);
    setImageLoaded(false);
    setImageError(false);
    setSelectedGst(outward.gst_address ?? null);
  }, [open, outward.id]);

  useEffect(() => {
    if (!open || !kazamWarehouseId) {
      set_gst_address(undefined);
      return;
    }

    const fetchGstData = async () => {
      try {
        const fetchGsts = await fetchGet(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/gst/get-gsts/${kazamWarehouseId}`,
        );

        if (fetchGsts) {
          set_gst_address(fetchGsts.data);
        }
      } catch (err: any) {
        showToast({
          type: ToastType.error,
          text: err,
        });
        console.error("Error:", err);
      }
    };

    fetchGstData();
  }, [open, kazamWarehouseId]);

  useEffect(() => {
    if (!open || !outward?.id) {
      setDcPrefix("");
      setDcSeq("");
      setSuggestedSeq("");
      return;
    }

    const fetchNextDcNo = async () => {
      try {
        const res = await fetchGet(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/next-dc-no/${outward.id}`,
        );
        console.log("[next-dc-no] response:", res);
        if (res?.success && res?.data) {
          const prefix: string = res.data.prefix ?? "";
          const fullDc: string = res.data.dc_no ?? "";
          const seq = fullDc.startsWith(prefix)
            ? fullDc.slice(prefix.length)
            : "";
          setDcPrefix(prefix);
          setDcSeq(seq);
          setSuggestedSeq(seq);
        } else {
          console.error("[next-dc-no] failed:", res);
        }
      } catch (err: any) {
        console.error("[next-dc-no] error:", err);
      }
    };

    fetchNextDcNo();
  }, [open, outward?.id]);

  /* -------------------- GENERATE DC -------------------- */
  const handleGenerateDC = async () => {
    try {
      const nextErrors: { narration?: string; dcSeq?: string; gst?: string } =
        {};
      if (!narration.trim()) {
        nextErrors.narration = "Narration is required";
      }
      // The list is only returned for multi-GST states (e.g. Uttar Pradesh),
      // where the backend cannot pick the Kazam GST on its own.
      if (gst_address?.length > 0 && !selectedGst) {
        nextErrors.gst = "Please select a GST address";
      }
      if (dcPrefix && !/^\d{3}$/.test(dcSeq.trim())) {
        nextErrors.dcSeq = "DC sequence must be 3 digits (e.g. 010)";
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        return;
      }
      setLoading(true);
      setImageLoaded(false);
      setImageError(false);

      const endpoint = outward.type === "SO" ? "generate-dc" : "generate-dc-st";
      const url = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/${endpoint}/${outward.id}`;

      const trimmedSeq = dcSeq.trim();
      const dcNoOverride =
        dcPrefix && trimmedSeq && trimmedSeq !== suggestedSeq
          ? `${dcPrefix}${trimmedSeq}`
          : undefined;

      const payload = {
        po_number: poNumber?.trim() || null,
        narration: narration.trim(),
        gst_address: selectedGst || undefined,
        dc_no: dcNoOverride,
      };

      const response = await fetchPut(url, payload);

      if (!response.success) {
        throw new Error(response?.error || "DC generation failed");
      }

      if (
        !response?.data?.image_preview_url ||
        !response?.data?.pdf_download_url
      ) {
        throw new Error("DC URLs missing from response");
      }

      setDcUrls({
        pdfDownloadUrl: response?.data?.pdf_download_url,
        imagePreviewUrl: response?.data?.image_preview_url,
        fileName: response?.data?.file_name,
        dcNo: response?.data?.dc_data?.dc_no,
      });

      setStep("PREVIEW");
      showToast({ type: ToastType.success, text: "DC generated successfully" });
    } catch (err: any) {
      const message =
        err?.response?.error || err?.message || "Failed to generate DC";
      showToast({
        type: ToastType.error,
        text: message,
      });
      console.error("DC Generation Error:", err);
    } finally {
      setLoading(false);
    }
  };

  /* -------------------- CONFIRM & MARK READY -------------------- */
  const handleConfirm = async () => {
    if (!dcUrls?.pdfDownloadUrl) {
      showToast({ type: ToastType.error, text: "DC URL missing" });
      return;
    }

    try {
      setLoading(true);

      const dcNo = dcUrls.dcNo;

      let uploadRes: any;
      let ambillRes: any | null = null;

      if (dcNo) {
        // First, create ambill
        ambillRes = await fetchPost(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/create-ambill-dc/${outward.id}`,
          {
            dc_no: dcNo,
            gst_address: selectedGst || undefined,
          },
        );

        // Check if ambill succeeded before uploading DC
        if (!ambillRes?.success) {
          const data = ambillRes?.data;
          const backendError =
            (data && typeof data === "object" && data.error) ||
            (typeof data === "string" ? data : null) ||
            ambillRes?.error ||
            "Failed to create AM Bill DC";
          throw new Error(backendError);
        }

        // Only upload DC if ambill succeeded
        uploadRes = await fetchPost(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/outward-inventory/upload-dc/${outward.id}`,
          {
            url: dcUrls.pdfDownloadUrl,
            originalname: `DC-${outward.id}.pdf`,
            dc_no: dcUrls.dcNo,
          },
        );
      }

      if (!uploadRes?.success) {
        const backendError =
          uploadRes?.error ||
          (typeof uploadRes?.data === "string" ? uploadRes.data : "") ||
          "DC update failed";
        throw new Error(backendError);
      }

      // Show ambill success message if available
      if (dcNo && ambillRes) {
        showToast({
          type: ToastType.success,
          text: ambillRes.message || "Delivery challan created successfully",
        });
      }

      // Show DC upload success message
      showToast({
        type: ToastType.success,
        text: "DC uploaded successfully",
      });

      onSuccess();
      onClose();
    } catch (err: any) {
      const message =
        err?.response?.error ||
        err?.error ||
        err?.message ||
        "Failed to confirm DC. Please try again.";
      showToast({
        type: ToastType.error,
        text: message,
      });
      console.error("DC Confirmation Error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  const downloadPdf = async () => {
    if (!dcUrls?.pdfDownloadUrl) return;
    const response = await fetch(dcUrls.pdfDownloadUrl);
    const blob = await response.blob();

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = dcUrls.fileName || `DC-${outward.id}.pdf`;
    document.body.appendChild(a);
    a.click();

    a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!open) return null;

  return (
    <div
      id="warehouse-finance-generate-dc-modal"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-40 overflow-y-auto p-4 sm:p-8"
    >
      <div className="relative w-full sm:max-w-2xl max-h-[85vh] bg-white shadow-lg flex flex-col rounded-lg wh-modal-xs">
        {/* Header - Fixed */}
        <div className="border-b px-6 py-4">
          <h2 className="text-lg font-semibold">Generate Delivery Challan</h2>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-6">
          {step === "FORM" && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded border bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500 mb-1">Outward ID</p>
                  <p className="text-sm font-medium text-gray-900">
                    {outward.id}
                  </p>
                  {outward.type === "SO" && outward.order_id && (
                    <p className="text-sm font-medium text-gray-900">
                      {outward.order_id}
                    </p>
                  )}
                </div>

                <div className="rounded border bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500 mb-1">Outward Type</p>
                  <p className="text-sm font-medium text-gray-900">
                    {outward.type}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="rounded border bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500 mb-1">From:</p>
                  <p className="text-sm font-medium text-gray-900">
                    {getOutwardSourceLabel(outward)}
                  </p>
                  {getOutwardSourceAddress(outward) && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatAddress(getOutwardSourceAddress(outward))}
                    </p>
                  )}
                </div>

                <div className="rounded border bg-gray-50 px-4 py-3">
                  <p className="text-xs text-gray-500 mb-1">To:</p>
                  <p className="text-sm font-medium text-gray-900">
                    {getOutwardDestinationLabel(outward)}
                  </p>
                </div>
              </div>
              {/* PO No & Narration */}
              <div className="space-y-4">
                {/* Purpose*/}
                {outward.type === "ST" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purpose
                    </label>
                    <p className="w-full px-3 py-2 text-sm  focus:ring-2 focus:ring-blue-500">
                      {outward.purpose}
                    </p>
                  </div>
                )}
                {outward.type === "ST" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Contact Details
                    </label>
                    <p className="w-full px-3 py-2 text-sm  focus:ring-2 focus:ring-blue-500">
                      {outward?.contact_name || ""} -{" "}
                      {outward?.contact_number || ""}
                    </p>
                  </div>
                )}

                {gst_address?.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      GST Address *
                    </label>

                    <select
                      id="warehouse-finance-generate-dc-gst-select"
                      className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                        errors.gst
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-blue-500"
                      }`}
                      value={selectedGst?.id || ""}
                      onChange={(e) => {
                        const gst = gst_address.find(
                          (g: any) => g.id === e.target.value,
                        );
                        setSelectedGst(gst);
                        if (gst && errors.gst) {
                          setErrors({ ...errors, gst: undefined });
                        }
                      }}
                    >
                      <option value="">Select GST Address</option>

                      {gst_address.map((gst: any) => (
                        <option key={gst.id} value={gst.id}>
                          {gst.gst_number} - {gst.address.city}
                        </option>
                      ))}
                    </select>
                    {errors.gst && (
                      <p className="mt-1 text-xs text-red-600">{errors.gst}</p>
                    )}
                  </div>
                )}
                {selectedGst && (
                  <div className="rounded border bg-gray-50 px-4 py-3 mt-3">
                    <p className="text-xs text-gray-500 mb-1">GST Address</p>

                    <p className="text-sm font-medium text-gray-900">
                      {selectedGst.name} ({selectedGst.id})
                    </p>

                    <p className="text-sm text-gray-700">
                      {selectedGst.address.line1}, {selectedGst.address.line2}
                    </p>

                    <p className="text-sm text-gray-700">
                      {selectedGst.address.city}, {selectedGst.address.state} -{" "}
                      {selectedGst.address.pincode}
                    </p>

                    <p className="text-sm text-gray-700">
                      GSTIN: {selectedGst.gst_number}
                    </p>
                  </div>
                )}
                {dcPrefix && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      DC Number
                    </label>
                    <div className="flex items-stretch w-full rounded-md border border-gray-300 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500">
                      <span className="px-3 py-2 text-sm bg-gray-100 text-gray-700 border-r border-gray-300 font-mono">
                        {dcPrefix}
                      </span>
                      <input
                        id="warehouse-finance-generate-dc-seq-input"
                        type="text"
                        inputMode="numeric"
                        maxLength={3}
                        value={dcSeq}
                        onChange={(e) => {
                          const v = e.target.value
                            .replace(/\D/g, "")
                            .slice(0, 3);
                          setDcSeq(v);
                          if (errors.dcSeq) {
                            setErrors({ ...errors, dcSeq: undefined });
                          }
                        }}
                        placeholder="010"
                        className={`flex-1 px-3 py-2 text-sm font-mono focus:outline-none ${
                          errors.dcSeq ? "text-red-600" : "text-gray-900"
                        }`}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Edit only if this number is already used on Ambill.
                    </p>
                    {errors.dcSeq && (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.dcSeq}
                      </p>
                    )}
                  </div>
                )}
                {/* PO Number (Optional) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PO Number
                  </label>
                  <input
                    id="warehouse-finance-generate-dc-po-input"
                    type="text"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                    placeholder="Enter PO number"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Narration (Mandatory) */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Narration *
                  </label>
                  <textarea
                    id="warehouse-finance-generate-dc-narration-textarea"
                    value={narration}
                    onChange={(e) => {
                      setNarration(e.target.value);
                      if (e.target.value.trim() && errors.narration) {
                        setErrors({ ...errors, narration: undefined });
                      }
                    }}
                    rows={3}
                    placeholder="Enter narration for delivery challan"
                    className={`w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                      errors.narration
                        ? "border-red-500 focus:ring-red-500"
                        : "border-gray-300 focus:ring-blue-500"
                    }`}
                  />
                  {errors.narration && (
                    <p className="mt-1 text-xs text-red-600">
                      {errors.narration}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {step === "PREVIEW" && dcUrls && (
            <div className="space-y-4">
              {/* Success Message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                    ✓
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-green-900 mb-1">
                      Delivery Challan generated successfully
                    </h3>
                    <p className="text-sm text-green-700">
                      Review the preview below and download if needed before
                      confirming.
                    </p>
                  </div>
                </div>
              </div>

              {/* View Mode Toggle & Actions */}
              <div className="flex items-center justify-between">
                <a
                  id="warehouse-finance-generate-dc-preview-link"
                  href={dcUrls.pdfDownloadUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  👁 Preview DC in next tab
                </a>

                <button
                  id="warehouse-finance-generate-dc-download-btn"
                  onClick={downloadPdf}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                >
                  ⬇ Download DC PDF
                </button>
              </div>

              <div className="flex flex-row justify-center items-center border rounded-lg overflow-hidden bg-gray-50">
                {!imageLoaded && !imageError && (
                  <div className="flex items-center justify-center bg-gray-100 min-h-[600px] w-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-sm text-gray-600">
                        Loading preview...
                      </p>
                    </div>
                  </div>
                )}

                {imageError && (
                  <div className="flex flex-col items-center justify-center min-h-[600px] bg-gray-100 p-8 w-full">
                    <svg
                      className="w-16 h-16 text-gray-400 mb-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <p className="text-gray-600 mb-4">
                      Failed to load preview image
                    </p>
                    <a
                      id="warehouse-finance-generate-dc-open-pdf-error-link"
                      href={dcUrls.imagePreviewUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Open PDF in new tab
                    </a>
                  </div>
                )}

                {imageLoaded && !imageError && (
                  <img
                    src={dcUrls.imagePreviewUrl}
                    alt="DC Preview"
                    className="w-auto h-auto"
                  />
                )}

                {!imageLoaded && !imageError && (
                  <img
                    src={dcUrls.imagePreviewUrl}
                    alt=""
                    onLoad={() => setImageLoaded(true)}
                    onError={() => {
                      setImageError(true);
                      setImageLoaded(true);
                    }}
                    className="hidden"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div className="border-t px-6 py-4 flex justify-end gap-2">
          {step === "FORM" && (
            <>
              <button
                id="warehouse-finance-generate-dc-cancel-btn"
                onClick={handleClose}
                disabled={loading}
                className={`px-4 py-2 border border-gray-300 rounded-md transition-all ${
                  loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              >
                Cancel
              </button>
              <button
                id="warehouse-finance-generate-dc-submit-btn"
                onClick={handleGenerateDC}
                disabled={loading}
                className={`px-4 py-2 bg-blue-600 text-white rounded-md transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-blue-700"
                }`}
              >
                {loading ? "Generating..." : "Generate DC"}
              </button>
            </>
          )}

          {step === "PREVIEW" && (
            <>
              <button
                id="warehouse-finance-generate-dc-back-btn"
                onClick={() => setStep("FORM")}
                disabled={loading}
                className={`px-4 py-2 border border-gray-300 rounded-md transition-all ${
                  loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"
                }`}
              >
                Back
              </button>
              <button
                id="warehouse-finance-generate-dc-confirm-btn"
                onClick={handleConfirm}
                disabled={loading}
                className={`px-4 py-2 bg-green-600 text-white rounded-md transition-all ${
                  loading
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-green-700"
                }`}
              >
                {loading ? "Confirming..." : "Confirm & Mark Ready"}
              </button>
            </>
          )}
        </div>
      </div>

      {loading && <LoadingWidget />}
    </div>
  );
}
